import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';
import { Manager, Popper, Reference, ReferenceChildrenProps } from 'react-popper';

import { Placement } from '@popperjs/core';

import { styled } from '../theme';
import ClickTrigger from './ClickTrigger';

const popoverRoot = document.getElementById('popover-root')!;

const Container = styled('div')(({ theme }) => ({
    zIndex: theme.zIndex.popovers,
    opacity: 0,
    animation: theme.animations.fadeIn,
}));

const View = styled('div')(({ theme }) => ({
    minWidth: 200,
    maxHeight: '50vh',
    overflowY: 'overlay' as any,
    backgroundColor: theme.color.white,
    boxShadow: theme.boxShadow.surface,
    borderRadius: theme.borderRadius.base,
    '&[data-placement*=bottom]': {
        marginTop: 16,
    },
    '&[data-placement*=top]': {
        marginBottom: 16,
    },
    '&[data-placement*=right]': {
        marginLeft: 16,
    },
    '&[data-placement*=left]': {
        marginRight: 16,
    },
}));

type RenderProps = { toggleVisibility: () => void; visibility: boolean };

type Props = Omit<React.ComponentProps<typeof Popper>, 'children'> &
    Pick<React.ComponentProps<typeof ClickTrigger>, 'eventType'> & {
        style?: React.CSSProperties;
        className?: string;
        children: (renderProps: RenderProps & ReferenceChildrenProps) => React.ReactNode;
        renderContent: (renderProps: RenderProps) => React.ReactNode;
        placement?: Placement;
        disableHideOnInnerClick?: boolean;
        disableHideOnOuterClick?: boolean;
        containerClassName?: string;
    };

const Popover = (
    {
        style,
        className,
        containerClassName,
        children,
        renderContent,
        placement = 'auto-start',
        disableHideOnInnerClick = false,
        disableHideOnOuterClick = false,
        eventType,
        ...props
    },
    ref
) => {
    const [visibility, setVisibility] = useState(false);

    const toggleVisibility = () => {
        setVisibility((value) => !value);
    };

    useImperativeHandle(ref, () => ({
        setPopoverVisibility: setVisibility,
    }));

    const PopoverPortal = useCallback(
        ({ children }) =>
            createPortal(
                <ClickTrigger
                    onOuterClick={() => {
                        !disableHideOnOuterClick && setVisibility(false);
                    }}
                    onInnerClick={() => {
                        !disableHideOnInnerClick && setVisibility(false);
                    }}
                    eventType={eventType}
                >
                    {({ ref }) => children({ ref })}
                </ClickTrigger>,
                popoverRoot
            ),
        [visibility, disableHideOnOuterClick, disableHideOnInnerClick, eventType]
    );

    return (
        <Manager>
            <section style={{ position: 'relative' }} className={containerClassName}>
                <Reference>
                    {({ ref }) =>
                        children({
                            ref,
                            toggleVisibility,
                            visibility,
                        })
                    }
                </Reference>
                <PopoverPortal>
                    {({ ref }) =>
                        !visibility ? null : (
                            <div ref={ref} className={className}>
                                <Popper placement={placement} {...props}>
                                    {({
                                        ref: popperRef,
                                        style: popperStyle,
                                        placement,
                                        arrowProps,
                                    }) => (
                                        <Container
                                            ref={popperRef}
                                            style={{ ...popperStyle, ...style }}
                                            data-placement={placement}
                                        >
                                            <View data-placement={placement}>
                                                {renderContent({
                                                    toggleVisibility,
                                                    visibility,
                                                })}
                                            </View>
                                        </Container>
                                    )}
                                </Popper>
                            </div>
                        )
                    }
                </PopoverPortal>
            </section>
        </Manager>
    );
};

export default forwardRef<{ setPopoverVisibility: (value: boolean) => void }, Props>(Popover);
